import { toast } from 'components/ui/toast-store'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Upload } from 'lucide-react'
import { Button } from 'components/ui/button'
import { clearError } from 'slices/documentLibrarySlice'
import {
	DocumentLibraryList,
	UploadFilesDialog,
	VersionHistoryDialog,
	DocumentPreviewDialog,
	DocumentShareDialog,
	DocumentEditSheet,
	matchesScope,
	type DocumentLibraryState,
	type LibraryDocument,
	type LibraryScopeArgs,
} from 'features/DocumentLibrary'

const ProjectDocuments: React.FC = () => {
	// Two URL patterns reach this component:
	//   `/projects/:id/files` (standalone) — `:id` is the project id
	//   `/locations/:id/projects/:projectId/files` (location-nested) —
	//     `:id` is the *location* id; the project id lives in
	//     `:projectId`. Prefer the nested param so the nested view
	//     hits the right project; fall back to `:id` for the
	//     standalone route.
	const { id, projectId: nestedProjectId } = useParams<{
		id?: string
		projectId?: string
	}>()
	const projectId = nestedProjectId || id || ''
	const dispatch = useDispatch()

	const scopeArgs: LibraryScopeArgs = { scope: 'project', projectId }

	const project = useSelector((s: any) => s.projects.entities[projectId])
	const sliceError = useSelector(
		(state: { libraryDocuments: DocumentLibraryState }) =>
			state.libraryDocuments.error,
	)

	// Surface slice errors as a toast. Errors that fire silently (Delete,
	// rename, share) used to leave the user wondering whether the click
	// took effect — this makes failures obvious. Auto-clear so the toast
	// doesn't re-fire on every render.
	// Notifications go through the shared toast store (MYD-561), titled as before.
	const showToast = ({ message }: { message: string }) =>
		toast.error(message, { title: 'Something went wrong' })

	useEffect(() => {
		if (sliceError) {
			showToast({ message: sliceError })
			dispatch(clearError())
		}
	}, [sliceError, dispatch])

	// `existingDocuments` lets UploadFilesDialog detect filename collisions
	// and route those uploads through the new-version path.
	const existingDocuments = useSelector(
		(state: { libraryDocuments: DocumentLibraryState }) => {
			const { entities, ids } = state.libraryDocuments
			return ids
				.map(_id => entities[_id])
				.filter(
					(d): d is LibraryDocument =>
						!!d && matchesScope(d, scopeArgs) && !d.isDeleted,
				)
				.map(d => ({ id: d._id, libraryName: d.libraryName }))
		},
	)

	const [uploadOpen, setUploadOpen] = useState(false)
	const [versionsDocId, setVersionsDocId] = useState<string | null>(null)
	const [previewDocId, setPreviewDocId] = useState<string | null>(null)
	const [shareTarget, setShareTarget] = useState<
		{ documentId: string; versionId?: string } | null
	>(null)
	const [editDocId, setEditDocId] = useState<string | null>(null)

	const openUpload = useCallback(() => setUploadOpen(true), [])

	const handleShare = useCallback(
		(documentId: string, versionId?: string) => {
			setShareTarget({ documentId, versionId })
		},
		[],
	)

	const handleEditUploadNewVersion = useCallback(() => {
		// Edit Sheet asks for a new version → close the sheet and reuse the
		// upload dialog. The dialog matches the picked file's name against
		// existingDocuments and prompts for a change note when it lines up.
		setEditDocId(null)
		setUploadOpen(true)
	}, [])

	const headerSubtitle = useMemo(
		() =>
			'Upload, version, preview, and share documents for this project. Each upload is stored with its original filename and full version history.',
		[],
	)

	if (!project) return null

	return (
		<div>
			<div className="flex flex-wrap justify-between items-center gap-4 mb-6">
				<div className="flex flex-col gap-1">
					<h2 className="text-2xl font-bold m-0">Documents</h2>
					<span className="text-sm text-gray-600">{headerSubtitle}</span>
				</div>
				<Button onClick={openUpload}>
					<Upload className="w-4 h-4 mr-1" />
					<span>Upload Document</span>
				</Button>
			</div>

			<DocumentLibraryList
				{...scopeArgs}
				onPreview={setPreviewDocId}
				onShowVersions={setVersionsDocId}
				onShare={handleShare}
				onRowClick={setEditDocId}
				onUpload={openUpload}
			/>

			<UploadFilesDialog
				open={uploadOpen}
				onClose={() => setUploadOpen(false)}
				scopeArgs={scopeArgs}
				existingDocuments={existingDocuments}
			/>

			<VersionHistoryDialog
				open={!!versionsDocId}
				onClose={() => setVersionsDocId(null)}
				documentId={versionsDocId}
				onShareVersion={handleShare}
			/>

			<DocumentPreviewDialog
				open={!!previewDocId}
				onClose={() => setPreviewDocId(null)}
				documentId={previewDocId}
			/>

			<DocumentShareDialog
				open={!!shareTarget}
				onClose={() => setShareTarget(null)}
				documentId={shareTarget?.documentId ?? null}
				versionId={shareTarget?.versionId}
			/>

			<DocumentEditSheet
				open={!!editDocId}
				onClose={() => setEditDocId(null)}
				documentId={editDocId}
				onUploadNewVersion={handleEditUploadNewVersion}
				onShare={handleShare}
				onShowVersions={setVersionsDocId}
			/>

		</div>
	)
}

export default ProjectDocuments
